// Shared building blocks for the simple site layout (header, footer, labels, post rows)
import { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import theme from '../theme';
import { useColorMode } from '../color-mode.js';

const t = theme;

// !important beats the global MUI font override in App.css
export const mono = `'IBM Plex Mono', ${t.typography.fontFamilyMono} !important`;

export const EMAIL = 'hello@arkadiuszjaneczko.com';

const navItems = [
  { text: 'Work', to: '/about-me/#work' },
  { text: 'Writing', to: '/writings/', match: '/writings' },
  { text: 'Contact', to: '/about-me/#contact' }
];

export const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short'
  });

export function TextLink({ href, to, children, external, active, download }) {
  return (
    <Link
      component={to ? RouterLink : 'a'}
      to={to}
      href={to ? undefined : href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      download={download}
      underline="none"
      sx={{
        fontFamily: mono,
        fontSize: t.typography.fontSize.sm,
        color: active ? t.colors.textPrimary : t.colors.textSecondary,
        transition: t.transitions.fast,
        '&:hover': { color: t.colors.textPrimary }
      }}
    >
      {children}
    </Link>
  );
}

// Inline icons keep the icon library out of the bundle
const iconProps = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true
};

const SunIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);

const MoonIcon = () => (
  <svg {...iconProps}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

function ColorModeToggle() {
  const { mode, toggle } = useColorMode();
  const next = mode === 'dark' ? 'light' : 'dark';

  return (
    <Box
      component="button"
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      sx={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
        color: t.colors.textSecondary,
        backgroundColor: 'transparent',
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.borderRadius.md,
        cursor: 'pointer',
        transition: t.transitions.fast,
        '&:hover': { color: t.colors.textPrimary, borderColor: t.colors.textMuted }
      }}
    >
      {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
    </Box>
  );
}

export function SiteHeader({ maxWidth = t.layout.contentWidth, sticky = true }) {
  const { pathname } = useLocation();

  return (
    <Box
      component="header"
      sx={{
        position: sticky ? 'sticky' : 'relative',
        top: 0,
        flexShrink: 0,
        zIndex: t.zIndex.sticky,
        backgroundColor: t.colors.headerBackground,
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${t.colors.border}`
      }}
    >
      <Box
        sx={{
          maxWidth,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Link
          component={RouterLink}
          to="/about-me/"
          underline="none"
          sx={{
            fontSize: '1.5rem',
            fontWeight: t.typography.fontWeight.bold,
            color: t.colors.textPrimary
          }}
        >
          aj.
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
          <Box component="nav" sx={{ display: 'flex', gap: { xs: 2, md: 3 } }}>
            {navItems.map((item) => (
              <TextLink
                key={item.text}
                to={item.to}
                active={item.match && pathname.startsWith(item.match)}
              >
                {item.text}
              </TextLink>
            ))}
          </Box>
          <ColorModeToggle />
        </Box>
      </Box>
    </Box>
  );
}

export function SiteFooter() {
  return (
    <Box
      component="footer"
      sx={{
        borderTop: `1px solid ${t.colors.border}`,
        py: 4,
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        fontFamily: mono,
        fontSize: t.typography.fontSize.xs,
        color: t.colors.textMuted
      }}
    >
      <span>© {new Date().getFullYear()} Arkadiusz Janeczko</span>
    </Box>
  );
}

// Small monospace label, e.g. "01 — Work"
export function SectionLabel({ index, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 3,
        fontFamily: mono,
        fontSize: t.typography.fontSize.xs,
        letterSpacing: t.typography.letterSpacing.wide,
        textTransform: 'uppercase',
        color: t.colors.textMuted
      }}
    >
      {index && <span>{index}</span>}
      {index && (
        <Box component="span" sx={{ width: 24, height: '1px', backgroundColor: t.colors.border }} />
      )}
      <span>{children}</span>
    </Box>
  );
}

export function PostRow({ post }) {
  return (
    <Link
      component={RouterLink}
      to={`/writings/${post.slug}`}
      underline="none"
      data-ga-id={`link-post-row-${post.slug}`}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '96px 1fr auto' },
        gap: { xs: 0.5, sm: 3 },
        alignItems: 'baseline',
        py: 2.5,
        borderTop: `1px solid ${t.colors.border}`,
        color: 'inherit',
        '&:hover .post-title': { color: t.colors.textPrimary },
        '&:hover .post-arrow': { transform: 'translateX(4px)', color: t.colors.textPrimary }
      }}
    >
      <Typography
        sx={{
          fontFamily: mono,
          fontSize: t.typography.fontSize.xs,
          color: t.colors.textMuted
        }}
      >
        {formatDate(post.date)}
      </Typography>
      <Box>
        <Typography
          className="post-title"
          sx={{
            fontSize: t.typography.fontSize.md,
            fontWeight: t.typography.fontWeight.medium,
            color: t.colors.textLight,
            transition: t.transitions.fast
          }}
        >
          {post.title}
        </Typography>
        {post.excerpt && (
          <Typography
            sx={{
              fontSize: t.typography.fontSize.sm,
              color: t.colors.textMuted,
              mt: 0.5
            }}
          >
            {post.excerpt}
          </Typography>
        )}
      </Box>
      <Box
        className="post-arrow"
        sx={{
          display: { xs: 'none', sm: 'block' },
          color: t.colors.textMuted,
          transition: t.transitions.fast
        }}
      >
        →
      </Box>
    </Link>
  );
}

export function PostList({ posts }) {
  return (
    <Box sx={{ borderBottom: `1px solid ${t.colors.border}` }}>
      {posts.map((post) => (
        <PostRow key={post.slug} post={post} />
      ))}
    </Box>
  );
}

export function Contact({ title = "Have a product to build? Let's talk." }) {
  const [copied, setCopied] = useState(false);

  const copyEmail = () => {
    navigator.clipboard?.writeText(EMAIL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: { xs: '1.5rem', md: '2rem' },
          fontWeight: t.typography.fontWeight.medium,
          letterSpacing: t.typography.letterSpacing.tight,
          color: t.colors.textPrimary,
          mb: 2
        }}
      >
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Link
          href={`mailto:${EMAIL}`}
          sx={{
            fontFamily: mono,
            fontSize: t.typography.fontSize.md,
            color: t.colors.textPrimary,
            textDecorationColor: t.colors.border,
            textUnderlineOffset: '4px',
            '&:hover': { textDecorationColor: t.colors.textPrimary }
          }}
        >
          {EMAIL}
        </Link>
        <Box
          component="button"
          type="button"
          onClick={copyEmail}
          sx={{
            fontFamily: mono,
            fontSize: t.typography.fontSize.xs,
            color: t.colors.textSecondary,
            backgroundColor: t.colors.backgroundCard,
            border: `1px solid ${t.colors.border}`,
            borderRadius: t.borderRadius.md,
            px: 1.25,
            py: 0.5,
            cursor: 'pointer',
            transition: t.transitions.fast,
            '&:hover': { color: t.colors.textPrimary, borderColor: t.colors.textMuted }
          }}
        >
          {copied ? 'copied ✓' : 'copy'}
        </Box>
      </Box>
    </Box>
  );
}
